import { difference } from 'lodash';
import { createSelector } from 'reselect';

import { selectConfigEngines } from '@common-modules/engines/selectors';
import { TdoTask } from '../models/DetailTDO';
import { ViewSettings } from '../models';
import { namespace } from '../store';
import { DetectionAssetState } from '../store.models';

import { DAG_REFERENCE_IDS } from '@helpers/constants';
import moment from 'moment';
import { selectStore as selectVoiceMaskStore } from '../../engines/voice-mask';
import { selectStore as selectSpeakerSeparationStore } from '../../engines/speaker-separation';

const selectStore = (s: { [namespace]: DetectionAssetState & ViewSettings }) =>
  s[namespace];

const selectTdo = createSelector(selectStore, (s) => s.tdo);

/**
 * Select TDO Tasks (up to default Graphql limit).
 */
export const selectTdoTasks = createSelector(
  selectTdo,
  (tdo) => tdo?.tasks?.records || []
);

/**
 * Select TDO Tasks for Redact's engines.
 */
export const selectTaskHistory = createSelector(
  selectTdoTasks,
  selectConfigEngines,
  (tasks, engines) => {
    const engineIds = Object.values(engines);
    return (tasks || []).filter((task) =>
      engineIds.some(
        (id) => task.engineId === id || task.engine.categoryId === id
      )
    );
  }
);

const COMPLETE_STATUSES: ReadonlyArray<string> = ['complete'];
const FAILED_STATUSES: ReadonlyArray<string> = [
  'aborted',
  'cancelled',
  'failed',
];

/**
 * Select completed tasks.
 */
export const selectCompletedTasks = createSelector(selectTaskHistory, (tasks) =>
  (tasks || []).filter((task) => COMPLETE_STATUSES.includes(task.status))
);

/**
 * Select failed tasks.
 */
export const selectFailedTasks = createSelector(selectTaskHistory, (tasks) =>
  (tasks || []).filter((task) => FAILED_STATUSES.includes(task.status))
);

/**
 * Select running tasks.
 */
export const selectRunningTasks = createSelector(
  selectTaskHistory,
  selectCompletedTasks,
  selectFailedTasks,
  (tasks, done, fail) => difference(tasks || [], [...done, ...fail]) || []
);

// This will only take most recent task and return if running
// export const selectMostRecentTaskByEngineIfRunning = createSelector(
//   selectTaskHistory,
//   selectCompletedTasks,
//   selectFailedTasks,
// (tasks, done, fail) => {
//   const tasksGroupByEngine = groupBy(tasks, (s) => s.engineId);
//   const latestLists = [];
//   for (const p in tasksGroupByEngine) {
//     if (Object.prototype.hasOwnProperty.call(tasksGroupByEngine, p)) {
//       const element = tasksGroupByEngine[p][0];
//       latestLists.push(element);
//     }
//   }
//   return difference(latestLists, [...done, ...fail]);
// }
// );

// /**
//  * Select Head Detection engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter(
      (task) =>
        task.engine.categoryId === id ||
        task.ioFolders?.some(
          (folder) => folder.referenceId === DAG_REFERENCE_IDS['owFromHead']
        )
    )
);

// /**
//  * Select Face Detection engines failed tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionFailed = createSelector(
  selectConfigEngines,
  selectFailedTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Face Detection engines completed tasks.
//  * @returns {Array<Task>}
//  */
export const selectDetectionCompleted = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  ({ detectionCategory: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Optical Tracking engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectTrackingRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  (
    {
      opticalTrackingEngineCatagoryId: id,
      opticalTrackingEngineCategoryId: id2,
    },
    tasks
  ) =>
    (tasks || []).filter(
      (task) => task.engine.categoryId === id || task.engine.categoryId === id2
    )
);

// /**
//  * Select Blur Redaction engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectRedactionRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ redactEngineId: id }, completedTasks, runningTasks) => {
    const running = (runningTasks || []).filter((task) => task.engineId === id);
    const completed = (completedTasks || []).filter(
      (task) => task.engineId === id
    );

    // only return running tasks newer than last completed task
    const completedTask = completed[0];
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

// /**
//  * Select Voice Mask engines running tasks.
//  * @returns {Array<Task>}
//  * TODO combine duplicate code from similar functions into helper function
//  */
export const selectVoiceMaskRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ voiceMaskEngineId: id }, completedTasks, runningTasks) => {
    const running = (runningTasks || []).filter((task) => task.engineId === id);
    const completed = (completedTasks || []).filter(
      (task) => task.engineId === id
    );

    // only return running tasks newer than last completed task
    const completedTask = completed[0];
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

// /**
//  * Selector to determine if head and object detections has been completed
//  * @returns {boolean}
//  */
export const selectHasHeadDetectionCompleted = createSelector(
  selectCompletedTasks,
  (completedTasks) => {
    const headDetectionTaskCompleted = (completedTasks || []).find(
      (task) =>
        task.engine.name &&
        task.engine.name.includes('Head Detection') &&
        COMPLETE_STATUSES.includes(task.status)
    );
    const outputWriterTaskCompleted = (completedTasks || []).find(
      (task) =>
        task.engine.name &&
        task.engine.name.includes('Output Writer') &&
        task.ioFolders?.some(
          (folder) => folder.referenceId === DAG_REFERENCE_IDS['owFromHead']
        ) &&
        COMPLETE_STATUSES.includes(task.status)
    );
    if (headDetectionTaskCompleted && outputWriterTaskCompleted) {
      return true;
    }
    return false;
  }
);

// /**
//  * Select Transcription engines running tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  ({ transcriptionCategoryId: id }, completedTasks, runningTasks) => {
    // preferable to just use the transcription output writer if can be found
    let running = (runningTasks || []).filter((task) =>
      task.ioFolders?.some(
        (folder) =>
          folder.referenceId === DAG_REFERENCE_IDS['owFromTranscription']
      )
    );

    if (running.length === 0) {
      running = (runningTasks || []).filter(
        (task) => task.engine.categoryId === id
      );
    }

    const completed = (completedTasks || []).filter(
      (task) =>
        task.engine.categoryId === id ||
        task.ioFolders?.some(
          (folder) =>
            folder.referenceId === DAG_REFERENCE_IDS['owFromTranscription']
        )
    );

    // only return runningTasks newer than last completed task
    const completedTask = completed[0];
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

// /**
//  * Select Transcription engines failed tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionFailed = createSelector(
  selectConfigEngines,
  selectFailedTasks,
  ({ transcriptionCategoryId: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

// /**
//  * Select Transcription engines completed tasks.
//  * @returns {Array<Task>}
//  */
export const selectTranscriptionCompleted = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  ({ transcriptionCategoryId: id }, tasks) =>
    (tasks || []).filter((task) => task.engine.categoryId === id)
);

/**
 * Matches a speaker-separation task however it was started: by its own engine
 * id (the standalone job), by category, or by the output-writer ioFolder the
 * upload DAG hangs off it — the DAG path runs under the ingestion job, so the
 * engine id alone does not find it.
 */
const makeIsSpeakerSeparationTask =
  ({
    speakerDetectionCategoryId,
    speakerSeparationEngineId,
  }: {
    speakerDetectionCategoryId?: string;
    speakerSeparationEngineId?: string;
  }) =>
  (task: TdoTask) =>
    task.engine.categoryId === speakerDetectionCategoryId ||
    task.engineId === speakerSeparationEngineId ||
    task.ioFolders?.some(
      (folder) =>
        folder.referenceId === DAG_REFERENCE_IDS['owFromSpeakerSeparation']
    );

/**
 * The latest of a set of tasks by creation date.
 *
 * Asked explicitly rather than taken as `tasks[0]`: the TDO query fetches
 * `tasks(limit:, offset:)` with no `orderBy`, so "first" is whatever the API
 * happened to return. The two selectors below used to disagree about this —
 * one indexed, the other computed — and they are answering the same question.
 */
const newestTask = (tasks: ReadonlyArray<TdoTask>): TdoTask | undefined =>
  tasks.reduce<TdoTask | undefined>(
    (latest, task) =>
      !latest ||
      moment.utc(task.createdDateTime) > moment.utc(latest.createdDateTime)
        ? task
        : latest,
    undefined
  );

/**
 * Select Speaker Separation engines running tasks.
 */
export const selectSpeakerSeparationRunning = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  (engines, completedTasks, runningTasks) => {
    const isSpeakerSeparationTask = makeIsSpeakerSeparationTask(engines);

    const running = (runningTasks || []).filter(isSpeakerSeparationTask);
    const completed = (completedTasks || []).filter(isSpeakerSeparationTask);

    // only return running tasks newer than last completed task
    const completedTask = newestTask(completed);
    if (completedTask && running.length > 0) {
      return running.filter(
        (task) =>
          moment.utc(task.createdDateTime) >
          moment.utc(completedTask.createdDateTime)
      );
    }

    return running;
  }
);

/**
 * Speaker separation failed, and nothing has superseded it.
 *
 * A later completed or running task clears it: a retry in flight is not a
 * failure, and a success after a failure is a success. Needed because the
 * upload-DAG path has no poller of its own — the duck only watches jobs it
 * created, so a DAG-run failure is otherwise entirely silent.
 */
export const selectSpeakerSeparationFailed = createSelector(
  selectConfigEngines,
  selectCompletedTasks,
  selectRunningTasks,
  selectFailedTasks,
  (engines, completedTasks, runningTasks, failedTasks) => {
    const isSpeakerSeparationTask = makeIsSpeakerSeparationTask(engines);

    const failed = (failedTasks || []).filter(isSpeakerSeparationTask);
    if (!failed.length) {
      return false;
    }

    const latestFailed = newestTask(failed);
    const superseding = [
      ...(completedTasks || []).filter(isSpeakerSeparationTask),
      ...(runningTasks || []).filter(isSpeakerSeparationTask),
    ];

    return !superseding.some(
      (task) =>
        moment.utc(task.createdDateTime) >
        moment.utc(latestFailed!.createdDateTime)
    );
  }
);

/**
 * Select Download engines running tasks.
 */
export const selectDownloadRunning = createSelector(
  selectConfigEngines,
  selectRunningTasks,
  ({ downloadEngineId: id }, tasks) =>
    (tasks || []).filter((task) => task.engineId === id)
);

export const selectAllDownloadTask = createSelector(
  selectConfigEngines,
  selectTaskHistory,
  ({ downloadEngineId: id }, tasks) =>
    (tasks || []).filter((task) => task.engineId === id)
);

export const selectAllRedactTask = createSelector(
  selectConfigEngines,
  selectTaskHistory,
  ({ redactEngineId, legacyRedactEngineId }, tasks) =>
    (tasks || []).filter(
      (task) =>
        task.engineId === redactEngineId ||
        task.engineId === legacyRedactEngineId
    )
);

export const selectVoiceMaskAssetLoading = createSelector(
  selectVoiceMaskStore,
  selectTdo,
  (voiceMaskStore, tdo) => {
    const voiceMaskJobStatus = voiceMaskStore[tdo?.id || ''] || {};

    // currently this just assumes the first job in the array the correct one - not sure if we could ever have more than one
    const voiceMaskJobsArr = Object.values(voiceMaskJobStatus);
    if (voiceMaskJobsArr.length) {
      const voiceMaskJob = voiceMaskJobsArr[0]!;
      return voiceMaskJob.isRunning; // this data structure updates the inRunning status same time asset gets loaded
    }
    return false;
  }
);

export const selectSpeakerSeparationAssetLoading = createSelector(
  selectSpeakerSeparationStore,
  selectSpeakerSeparationRunning,
  selectTdo,
  (speakerSeparationStore, runningTasks, tdo) =>
    // `?.` on the store itself, not just the per-TDO lookup: stores that don't
    // register the speaker-separation slice (component test harnesses, and any
    // host that mounts the transcript tab without it) would otherwise throw
    // here the moment anything reads this selector.
    Object.values(speakerSeparationStore?.[tdo?.id || ''] || {}).some(
      (job) => job.isRunning
    ) || runningTasks.length > 0
);
